<script setup lang="ts">
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import CompanyBadge from '@/vue/atoms/company-badge.vue';
import RecommendedPill from '@/vue/atoms/recommended-pill.vue';
import ConsumedMarker from '@/vue/atoms/consumed-marker.vue';
import MediaPlayBadge from '@/vue/atoms/media-play-badge.vue';
import ExploreLibraryCard from '@/vue/molecules/recco/explore-library-card.vue';
import type {Content} from '@/ts/types/dto/interventions-dto';
import type {ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {
  contentMediaType, isPlayableMediaType, mediaLabelWithDuration, mediaTypeLabelKey, statusMarkerLabelKey,
} from '@/ts/utils/recco-label-keys';
import {buildBandEntries, type BandEntry} from '@/ts/utils/band-entries';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {useDragToScroll} from '@/ts/composables/pure/use-drag-to-scroll';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import {withConsumed} from '@/ts/utils/consumed-overlay';

const props = withDefaults(defineProps<{
  recommendations: Content[];
  title: string;
  backLabel: string;
  // When true, every card carries the "Recommended" marker — the whole band is
  // recommendation-driven (the Explore "For you" rail).
  markRecommended?: boolean;
  // Recommended challenges mixed into the rail (IPD-2836: "max 3, mixed in with the
  // other content") — one is slotted in after every third content card.
  challenges?: ExploreLibraryItemDTO[];
}>(), {
  markRecommended: false,
  challenges: () => [],
});

const {t: $t} = useI18n();
const {scrollAreaRef, onMouseDown} = useDragToScroll();
const viewedContent = useViewedContentStore();

// Applied before the entries are built so the marker and the accessible name can't disagree.
// Challenges are skipped: they carry no content id and have no consumed marker.
const viewedRecommendations = computed<Content[]>(() =>
  props.recommendations.map((content) => withConsumed(content, viewedContent.hasViewed(content.id))));

const bandEntries = computed<BandEntry[]>(() => buildBandEntries(viewedRecommendations.value, props.challenges));

// Check if we should show this band
const shouldShowBand = computed(() => {
  return bandEntries.value.length > 0;
});

// Media-type verb label, derived through the shared helper so it can't drift from
// the consumed-marker mapping.
function getContentCategory(content: Content): string {
  // "Listen · 12 min" (IPD-3375), and the same text goes into the accessible name below.
  return mediaLabelWithDuration($t(mediaTypeLabelKey[contentMediaType(content)]), content.duration, $t);
}

// Mirrors the visual chip precedence: consumed replaces "Recommended" and subsumes the media verb.
function getContentAriaLabel(content: Content): string {
  if (content.consumed) {
    return `${$t(statusMarkerLabelKey[contentMediaType(content)])}, ${content.name ?? $t('untitled')}`;
  }
  const prefix = props.markRecommended ? `${$t('recco_recommended')}, ` : '';
  return `${prefix}${content.name ?? $t('untitled')} - ${getContentCategory(content)}`;
}

// Get route link for content with back label state
function getContentLink(content: Content): { name: string; params: { itemId: string }; state: { backLabel: string } } {
  return {
    name: 'block-content',
    params: {
      itemId: content.id,
    },
    state: {
      backLabel: props.backLabel,
    },
  };
}

</script>

<template>
  <div v-if="shouldShowBand" class="block-content-band">
    <div class="band-header">
      <h3 class="band-title">
        {{ title }}
      </h3>
    </div>

    <q-scroll-area ref="scrollAreaRef" class="content-scroll-container">
      <div class="content-items" @mousedown="onMouseDown">
        <template v-for="(entry, index) in bandEntries" :key="entry.key">
          <router-link
            v-if="entry.content"
            :to="getContentLink(entry.content)"
            class="content-item"
            :aria-label="getContentAriaLabel(entry.content)"
          >
            <div class="content-image">
              <lazy-image
                v-if="entry.content.image"
                :src="calculateMediaUrl(entry.content.image)"
                :alt="entry.content.name"
                :loading="index < 6 ? 'eager' : 'lazy'"
                :width="246"
                format="webp"
                class="item-image"
              />
              <media-play-badge v-if="isPlayableMediaType(contentMediaType(entry.content))"/>
              <consumed-marker v-if="entry.content.consumed" :media-type="contentMediaType(entry.content)"/>
              <recommended-pill v-else-if="markRecommended"/>
              <company-badge :company-image="entry.content.companyImage"/>
            </div>

            <div class="content-info">
              <div class="content-category">
                <span>{{ getContentCategory(entry.content) }}</span>
              </div>
              <h3 class="content-title">
                {{ entry.content.name ?? $t('untitled') }}
              </h3>
            </div>
          </router-link>
          <!-- Recommended challenge mixed into the rail as a compact card -->
          <explore-library-card
            v-else-if="entry.challenge"
            :item="entry.challenge"
            :back-label="backLabel"
          />
        </template>
      </div>
    </q-scroll-area>
  </div>
</template>

<style lang="scss" scoped>
.block-content-band {
  width: 100%;
  margin-top: var(--Spacing-spacing-8);
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.band-header {
  padding: 0 var(--Spacing-spacing-5);

  @include screen-md {
    padding: 0;
  }
}

.band-title {
  @include font-h3-bold;
  color: var(--Neutral-UI-neutral-ui-8);
  margin: 0;
}

.content-scroll-container {
  // Fixed height needed for q-scroll-area
  // Mobile: 150px image + ~80px info + padding + focus ring space
  height: 310px;
  padding: 0 var(--Spacing-spacing-5) var(--Spacing-spacing-1);

  @include screen-md {
    // Desktop: 188px image + ~80px info + padding + focus ring space
    height: 350px;
    padding: 0 0 var(--Spacing-spacing-1);
  }
}

.content-items {
  display: flex;
  gap: var(--Spacing-spacing-4);
  // Padding to accommodate focus ring
  padding: 6px 4px var(--Spacing-spacing-2) 4px;
  /* Ensure items don't wrap and allow horizontal scrolling */
  flex-wrap: nowrap;
  cursor: grab;
  user-select: none;
}

.content-item {
  /* Make items smaller on mobile, larger on desktop */
  flex: 0 0 200px;
  border-radius: var(--card-border-radius);
  overflow: hidden;
  transition: transform 0.2s ease;
  text-decoration: none;
  color: inherit;
  display: block;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus {
    outline: 2px solid var(--Primary-primary-color-2);
    outline-offset: 2px;
  }

  @include screen-md {
    flex: 0 0 var(--Grid-Responsiveness-col-2-2-2, 246px);
  }
}

.content-image {
  position: relative;
  width: 100%;
  height: 150px;
  background-color: var(--Neutral-UI-neutral-ui-1);
  border-radius: var(--card-border-radius);
  overflow: hidden;

  @include screen-md {
    height: var(--Spacing-spacing-12, 188px);
  }
}

.item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}


.content-info {
  padding: var(--Spacing-spacing-4) 0 6px;
}

.content-category {
  @include font-body-sm;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  color: var(--Neutral-UI-neutral-ui-60);
  margin-bottom: 0;
}

.content-title {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-8);
  margin: 0;
  line-height: 1.4;
  font-weight: 600;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>